<template>
    <Modal
        id="modal-checkin"
        size="xl"
        :title="$t('checkIn.checkIn')"
        :okTitle="$t('modal.save')"
        :okDisabled="isSubmitting"
        :cancelTitle="$t('modal.cancel')"
        :cancelDisabled="isSubmitting"
        :noCloseOnEsc="true"
        :noCloseOnBackdrop="true"
        @okHandler="onSubmit"
        @hideHandler="onCloseModal"
        isModalRoute
    >
        <Loader :loader="loader" />
        <b-form @submit="onSubmit" v-if="!loader">
            <b-row>
                <b-col sm="12" md="6" xl="3">
                    <c-satisfaction :label="$t('checkIn.howAmIFeeling')" :value.sync="$v.form.satisfaction.$model" />
                </b-col>

                <b-col sm="12" md="8" xl="6">
                    <div class="mb-4">
                        <label>{{ $t('pdis.checkins.form.labels.status') }}</label>
                        <br />
                        <b-button-group>
                            <b-button
                                v-for="status in statusOptions"
                                :key="status.value"
                                @click="onClickRange(status)"
                                :pressed="isButtonPressed(status.value)"
                                v-b-popover.hover.bottom="$t(`pdis.checkins.form.labels.${status.value}`)"
                                :variant="`outline-${status.color}`"
                            >
                                {{ $t(`pdis.checkins.form.labels.${status.value}`) }}
                            </b-button>
                        </b-button-group>
                    </div>
                </b-col>
                <b-col sm="12" md="12" xl="3">
                    <c-initial-value
                        :state="checkState('currentValue')"
                        :value.sync="$v.form.currentValue.$model"
                        :disabled="isDisabledCurrentValue"
                        :label="$t('pdis.checkins.form.labels.current_value')"
                        :placeholder="$t('pdis.checkins.form.inputs.placeholder.current_value')"
                    >
                        <span v-if="!$v.form.currentValue.decimal">
                            {{ $t('form.validations.decimal') }}
                        </span>
                    </c-initial-value>
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <c-description
                        :state="checkState('comments')"
                        :value.sync="$v.form.comments.$model"
                        :label="$t('pdis.checkins.form.questionComment')"
                        :placeholder="$t('checkIn.generalComments')"
                    />
                </b-col>
            </b-row>
            <b-row>
                <b-col sm="12" md="7" xl="5">
                    <b-form-group :label="$t('form.label.targetFile')" label-class="font-weight-bold">
                        <b-form-file
                            ref="file-input"
                            v-model="selectedFile"
                            :placeholder="$t('form.placeholder.chooseFile')"
                            :accept="acceptedExtensions"
                            @change="onFileSelected"
                            :browse-text="$t('form.button.search')"
                        ></b-form-file>
                        <small v-if="fileIsTooLarge" class="text-danger">{{
                            $t('targets.informations.sizeFileMax')
                        }}</small>
                        <small v-if="invalidFileExtension" class="text-danger">
                            {{ $t('form.validations.invalidExtension') }}
                        </small>
                    </b-form-group>
                </b-col>
            </b-row>
        </b-form>
    </Modal>
</template>

<script>
import { EventBuzz } from '@/main'
import Loader from '@/views/components/loader'
import i18n from '@/i18n'
import Modal from '@/views/components/modal'
import notification from '@/lib/notification'
import { validationMixin } from 'vuelidate'
import { getParam } from '@/lib/url'
import { required, decimal } from 'vuelidate/lib/validators'
import { Description as CDescription, InitialValue as CInitialValue } from '@/views/components/fields'
import { CSatisfaction } from './components/fields'
import api from '@/services/api'
import { mapperPdiCheckinToApi } from './mappers/mapperPdiCheckinToAPI'
import { success, danger } from '@/lib/notification'
import defaultPdiStatusOptions from '@/data/pdiStatus'
import Swal from 'sweetalert2'
notification.init()

const initialState = {
    comments: '',
    satisfaction: '',
    currentValue: '',
    statusOptions: [],
}

const allowedExtensions = [
    'doc',
    'docx',
    'xls',
    'xlsx',
    'ppt',
    'pptx',
    'pdf',
    'png',
    'jpg',
    'ods',
    'odt',
    'odp',
    'gif',
    'txt',
    'pst',
    'ost',
]

export default {
    name: 'PdiCheckin',
    components: {
        Modal,
        Loader,
        CDescription,
        CSatisfaction,
        CInitialValue,
    },
    data() {
        return {
            isSubmitting: false,
            isDisabledCurrentValue: false,
            statusOptions: defaultPdiStatusOptions,
            actionPlanId: getParam('actionPlanId', this),
            loader: true,
            form: Object.assign({}, initialState),
            clicked: '',
            selectedFile: null,
            fileIsTooLarge: false,
            invalidFileExtension: false,
            value: 0,
        }
    },
    mounted() {
        this.loader = true

        api.get(`/action-plans/${this.actionPlanId}`)
            .then((resp) => {
                const {
                    data: { data },
                } = resp

                this.actionPlans = data

                this.form.status = this.actionPlans.status
                this.form.satisfaction = this.actionPlans.satisfaction
                this.form.currentValue = this.actionPlans.current_value
            })
            .catch((e) => {
                console.log(e)
                this.notificationError()
            })
            .finally(() => {
                this.loader = false
            })
    },
    destroyed() {
        this.$emit('modalClosed', 'pdi-check-in')
    },
    mixins: [validationMixin],
    validations: {
        form: {
            satisfaction: {},
            comments: {},
            currentValue: {
                required,
                decimal,
            },
            status: {},
            evidence: {},
        },
    },
    computed: {
        isEdit() {
            return this.actionPlanId
        },
        canEditCheckIn() {
            return this.permissions && this.current.level
                ? this.permissions.current_event_permissions.goals[this.current.level].edit_checkin
                : false
        },
        acceptedExtensions() {
            return allowedExtensions.map((ext) => '.' + ext).join(', ')
        },
        currentUser() {
            return this.$store.state.currentUser.user
        },
    },
    methods: {
        isButtonPressed(status) {
            if (this.clicked) {
                return this.clicked == status
            }
            return this.actionPlans && this.actionPlans.status === status
        },
        onClickRange(status) {
            this.isDisabledCurrentValue = false
            this.form.currentValue = 0
            if (status.value === 'finished') {
                this.form.currentValue = this.actionPlans.current_value

                Swal({
                    type: 'warning',
                    title: i18n.t('pdis.notification.youWantToSetTheValue'),
                    confirmButtonText: i18n.t('notification.yes'),
                    cancelButtonText: i18n.t('notification.no'),
                    showCancelButton: true,
                    showLoaderOnConfirm: true,
                    preConfirm: () => {
                        this.form.currentValue = 100

                        this.isDisabledCurrentValue = true

                        return this.form.currentValue
                    },
                })
            }

            if (status.value === 'not_started') {
                this.isDisabledCurrentValue = true
                this.form.currentValue = status.currentValue
            }
            this.form.status = status.value
            this.clicked = status.value
        },
        async onSubmit() {
            if (this.validate()) {
                this.loader = true

                await api
                    .post(`/action-plans/${this.actionPlanId}/checkins`, mapperPdiCheckinToApi(this.form))
                    .then(() => {
                        this.onUpload()
                        this.notificationPdiCheckinCreatedSuccessfully()
                        this.dispatchPdisList()
                        this.dispatchPdisPerformanceForPdiWidget()
                        this.dispatchUpdateParent()
                    })
                    .catch((e) => {
                        console.log(e)
                        this.notificationError()
                    })
                    .finally(() => {
                        this.loader = false
                    })
            }
        },
        async onUpload() {
            try {
                const formData = new FormData()
                formData.append('file', this.selectedFile, this.selectedFile.file)
                this.form.evidence = this.selectedFile

                await api.post(`/action-plans/${this.actionPlanId}/uploads`, formData)
                this.value = 0
            } catch (error) {
                this.value = 0
            } finally {
                this.selectedFile = null
            }
        },
        isLoading(state) {
            this.btnLoader = state
        },
        isValid() {
            return !this.$v.form.$anyError
        },
        validate() {
            this.$v.$touch()
            const invalidEvidence = this.evidence && (this.fileIsTooLarge || this.invalidFileExtension)
            return !invalidEvidence && this.isValid
        },
        checkState(val) {
            const field = this.$v.form[val]
            return field.$dirty ? !field.$invalid : null
        },
        onCloseModal() {
            this.$bvModal.hide()
        },
        canNotCreateOrEditCheckIn() {
            return this.isEdit ? !this.canEditCheckIn : !this.canDoCheckIn
        },
        notificationPdiCheckinCreatedSuccessfully() {
            success(
                i18n.t('notification.editedSuccessfully', {
                    item: i18n.t('pdis.actionPlan'),
                })
            )
        },
        notificationError() {
            danger()
        },
        dispatchPdisList() {
            let userId = this.$route.params.id || this.$route.query.userId || this.currentUser.id

            this.$store.dispatch('updatePdisList', {
                periods: this.$store.state.filters.periods,
                userId: userId,
            })
        },
        dispatchPdisPerformanceForPdiWidget() {
            EventBuzz.$emit('update-pdi-summary')
        },
        dispatchUpdateParent() {
            EventBuzz.$emit('update-parent')
            this.$emit('update-parent')
        },
        onFileSelected(event) {
            if (this.selectedFile) {
                this.selectedFile = event.target.files[0]
            }
        },
    },
    watch: {
        selectedFile: function () {
            const MAX_FILE_SIZE_ALLOWED = 20000000
            if (this.selectedFile) this.fileIsTooLarge = this.selectedFile.size > MAX_FILE_SIZE_ALLOWED
            const fileExtension = this.selectedFile.name.split('.').pop()
            this.invalidFileExtension = !allowedExtensions.includes(fileExtension.toLowerCase())
            let reader = new FileReader()
            reader.readAsDataURL(this.selectedFile)
            reader.onload = (event) => {
                this.form.evidence = {
                    blob: event.target.result,
                    filename: this.selectedFile.name,
                }
            }
        },
    },
}
</script>
